import * as apply from 'fp-ts/Apply';
import * as array from 'fp-ts/Array';
import * as chainable from 'fp-ts/Chain';
import * as e from 'fp-ts/Either';
import * as fe from 'fp-ts/FromEither';
import { constant, flow, identity, pipe } from 'fp-ts/function';
import { fold as foldO, none, some } from 'fp-ts/Option';
import { sign } from 'fp-ts/Ordering';
import { not } from 'fp-ts/Predicate';
import { separated } from 'fp-ts/Separated';
// TODO: Witherable
/**
 * @category Instances
 */
export const URI = 'Remote';
/**
 * @category Constructors
 */
export const initial = {
    _tag: 'initial',
};
/**
 * @category Constructors
 */
export const pending = {
    _tag: 'pending',
};
/**
 * @category Constructors
 */
export const done = (value) => ({
    _tag: 'done',
    value,
});
/**
 * @category Constructors
 */
export const of = done;
/**
 * @category Instances
 */
export const Pointed = {
    URI,
    of,
};
/**
 * @category Constructors
 */
export const zero = () => initial;
/**
 * @category Instances
 */
export const Zero = {
    URI,
    zero,
};
/**
 * @category Refinements
 */
export const isInitial = (data) => data._tag === 'initial';
/**
 * @category Refinements
 */
export const isPending = (data) => data._tag === 'pending';
/**
 * @category Refinements
 */
export const isDone = (data) => data._tag === 'done';
/**
 * @category pattern matching
 */
export const foldW = (onInitial, onPending, onDone) => (ma) => {
    switch (ma._tag) {
        case 'initial': {
            return onInitial();
        }
        case 'pending': {
            return onPending();
        }
        case 'done': {
            return onDone(ma.value);
        }
    }
};
/**
 * Needed for "unwrap" value from {@link Remote} "container".
 * It applies a function to each case in the data structure.
 *
 * @category pattern matching
 * @example
 * const onInitial = () => "it's initial"
 * const onPending = () => "it's pending"
 * const onDone = (data) => `${data + 1}`
 * const f = match(onInitial, onPending, onDone)
 *
 * f(initial) // "it's initial"
 * f(pending) // "it's pending"
 * f(done(21)) // '22'
 */
export const fold = foldW;
/**
 * @category mapping
 */
export const map = (f) => (fa) => isDone(fa) ? done(f(fa.value)) : fa;
/**
 * @category Instances
 */
export const Functor = {
    URI,
    map: (fa, f) => pipe(fa, map(f)),
};
/**
 * @category utils
 */
export const ap = (fa) => (fab) => {
    switch (fa._tag) {
        case 'pending': {
            return pending;
        }
        case 'initial': {
            return isPending(fab) ? fab : fa;
        }
        case 'done': {
            return isDone(fab) ? done(fab.value(fa.value)) : fab;
        }
    }
};
/**
 * @category Instances
 */
export const Apply = {
    ...Functor,
    ap: (fab, fa) => pipe(fab, ap(fa)),
};
/**
 * @category Instances
 */
export const Applicative = {
    ...Apply,
    ...Pointed,
};
/**
 * @category sequencing
 */
export const chain = (f) => (fa) => isDone(fa) ? f(fa.value) : fa;
/**
 * @category Instances
 */
export const Chain = {
    ...Apply,
    chain: (fa, f) => pipe(fa, chain(f)),
};
/**
 * @category Instances
 */
export const Monad = {
    ...Chain,
    ...Applicative,
};
/**
 * @category folding
 */
export const reduce = (b, f) => match2(() => b, (a) => f(b, a));
/**
 * @category folding
 */
export const reduceRight = (b, f) => match2(() => b, (a) => f(a, b));
/**
 * @category folding
 */
export const foldMap = (M) => (f) => (fa) => (isDone(fa) ? f(fa.value) : M.empty);
/**
 * @category Instances
 */
export const Foldable = {
    URI,
    reduce: (fa, b, f) => pipe(fa, reduce(b, f)),
    reduceRight: (fa, b, f) => pipe(fa, reduceRight(b, f)),
    foldMap: (M) => (fa, f) => pipe(fa, foldMap(M)(f)),
};
/**
 * @category traversing
 */
export const traverse = (F) => (f) => (ta) => isDone(ta) ? F.map(f(ta.value), done) : F.of(initial);
/**
 * @category traversing
 */
export const sequence = (F) => (ta) => isDone(ta) ? F.map(ta.value, done) : F.of(initial);
/**
 * @category Instances
 */
export const Traversable = {
    ...Functor,
    ...Foldable,
    sequence,
    traverse: (F) => (f, fab) => pipe(f, traverse(F)(fab)),
};
/**
 * @category error handling
 */
export const alt = (that) => (self) => (isDone(self) ? self : that());
/**
 * @category Instances
 */
export const Alt = {
    ...Functor,
    alt: (fa, that) => pipe(fa, alt(that)),
};
/**
 * @category Instances
 */
export const Alternative = {
    ...Applicative,
    ...Alt,
    ...Zero,
};
/**
 * @category utils
 */
export const extend = (f) => (fa) => (isDone(fa) ? done(f(fa)) : fa);
/**
 * @category Instances
 */
export const Extend = {
    ...Functor,
    extend: (wa, f) => pipe(wa, extend(f)),
};
/**
 * @category conversions
 */
export const fromEither = e.fold(() => initial, done);
/**
 * @category Instances
 */
export const FromEither = {
    URI,
    fromEither,
};
/**
 * @category utils
 */
export const apFirst = apply.apFirst(Apply);
/**
 * @category utils
 */
export const apSecond = apply.apSecond(Apply);
/**
 * @category do notation
 */
export const apS = apply.apS(Apply);
/**
 * @category do notation
 */
export const bind = chainable.bind(Chain);
/**
 * @category conversions
 */
export const fromOption = foldO(() => initial, done);
/**
 * @category conversions
 */
export const fromNullable = (a) => a != null ? done(a) : initial;
/**
 * @category conversions
 */
export const fromPredicate = (predicate) => (a) => predicate(a) ? done(a) : initial;
/**
 * @category lifting
 */
export const fromEitherK = fe.fromEitherK(FromEither);
/**
 * @category lifting
 */
export const fromOptionK = (f) => flow(f, fromOption);
/**
 * @category lifting
 */
export const fromNullableK = (f) => flow(f, fromNullable);
/**
 * @category filtering
 */
export const compact = chain(fromOption);
/**
 * @category internal
 */
const getLeft = (ma) => ma._tag === 'Right' ? initial : done(ma.left);
/**
 * @category internal
 */
const getRight = (ma) => ma._tag === 'Left' ? initial : done(ma.right);
/**
 * @category internal
 */
const defaultSeparated = separated(initial, initial);
/**
 * @category filtering
 */
export const separate = (ma) => (isDone(ma) ? separated(getLeft(ma.value), getRight(ma.value)) : defaultSeparated);
/**
 * @category Instances
 */
export const Compactable = {
    URI,
    compact,
    separate,
};
/**
 * @category filtering
 */
export const filter = (predicate) => (fa) => isDone(fa) ? (predicate(fa.value) ? fa : initial) : fa;
/**
 * @category filtering
 */
export const filterOrPending = (predicate) => (fa) => isDone(fa) ? (predicate(fa.value) ? fa : pending) : fa;
/**
 * @category internal
 */
const _filter = (fa, predicate) => pipe(fa, filter(predicate));
/**
 * @category filtering
 */
export const partition = (predicate) => (fa) => separated(_filter(fa, not(predicate)), _filter(fa, predicate));
/**
 * @category filtering
 */
export const partitionMap = (f) => flow(map(f), separate);
/**
 * @category filtering
 */
export const filterMap = (f) => (fa) => (isDone(fa) ? fromOption(f(fa.value)) : initial);
/**
 * @category Instances
 */
export const Filterable = {
    ...Functor,
    ...Compactable,
    filter: _filter,
    filterMap: (fa, f) => pipe(fa, filterMap(f)),
    partition: (fa, predicate) => pipe(fa, partition(predicate)),
    partitionMap: (fa, f) => pipe(fa, partitionMap(f)),
};
/**
 * @category sequencing
 */
export const flatten = chain(identity);
/**
 * @category sequencing
 */
export const chainOptionK = flow(fromOptionK, chain);
/**
 * @category sequencing
 */
export const chainNullableK = flow(fromNullableK, chain);
/**
 * @category sequencing
 */
export const chainEitherK = flow(fromEitherK, chain);
/**
 * @category sequencing
 */
export const chainFirst = chainable.chainFirst(Chain);
/**
 * @category sequencing
 */
export const chainFirstEitherK = fe.chainFirstEitherK(FromEither, Chain);
/**
 * @category sequencing
 */
export const chainFirstOptionK = flow(fromOptionK, chainFirst);
/**
 * @category Destructors
 */
export const getOrElseW = (onNone) => (fa) => (isDone(fa) ? fa.value : onNone());
/**
 * Takes a default value as an argument.
 * If this {@link Remote} is "Left" part it will return default value.
 * If this {@link Remote} is {@link Done} it will return its value ("wrapped" value, not default value)
 *
 * Note: Default value should be the same type as {@link Remote} (internal) value, if you want to pass different type as default, use {@link fold}.
 *
 * @example
 * getOrElse(() => 999)(some(1)) // 1
 * getOrElseValue(() => 999)(initial) // 999
 *
 * @category Destructors
 */
export const getOrElse = getOrElseW;
/**
 * A more concise way to "unwrap" values from {@link Remote} "container".
 * It uses fold in its implementation, collapsing `onInitial` and `onPending` on the `onUnresolved` handler.
 * When fold's `onInitial` returns, `onNode` is called with `none`.
 *
 * @category pattern matching
 * @example
 * const onUnresolved = (progressOption) => "no data to show"
 * const onDone = (data) => `result is: ${data + 1}`
 * const f = match2(onUnresolved, onDone)
 *
 * assert.equal(f(initial), "no data to show");
 * assert.equal(f(pending), "no data to show");
 * assert.equal(f(done(21)), "result is: 22");
 */
export const match2 = (onUnresolved, onDone) => fold(onUnresolved, onUnresolved, onDone);
/**
 * One more way to unwrap a value from {@link Remote}.
 * {@link Initial} and {@link Pending} will return `null`.
 * {@link Done} will return it's wrapped value.
 *
 * @category Destructors
 * @example
 * assert.equal(toNullable(done(2)), 2);
 * assert.equal(toNullable(initial), null);
 * assert.equal(toNullable(pending), null);
 */
export const toNullable = (ma) => (isDone(ma) ? ma.value : null);
/**
 * @category Destructors
 */
export const toUndefined = (ma) => (isDone(ma) ? ma.value : undefined);
/**
 * Convert {@link Remote} to {@link Option}
 * `Left` part will be converted to {@link import('fp-ts/Option').None None}.
 * {@link Done} will be converted to {@link import('fp-ts/Option').Some Some}.
 *
 * @category Destructors
 * @example
 * toOption(done(2)) // some(2)
 * toOption(initial) // none
 * toOption(pending) // none
 */
export const toOption = (data) => isDone(data) ? some(data.value) : none;
/**
 * Convert {@link Remote} to `Either`.
 * {@link Initial} and {@link Pending} will be converted to `Left<L>`.
 * Since they do not have `E` values,
 * you must provide a value of type `E` that will be used to construct
 * the `Left<E>` for those two cases.
 * {@link Done} will be converted to `Right<A>`.
 *
 * @example:
 * const f = toEither(
 * 		() => new Error('Data not fetched'),
 * 		() => new Error('Data is fetching')
 * )
 * f(done(2)) // right(2)
 * f(initial) // right(Error('Data not fetched'))
 * f(pending) // right(Error('Data is fetching'))
 */
export const toEither = (onInitial, onPending) => foldW(flow(onInitial, e.left), flow(onPending, e.left), e.right);
/**
 * Compare values and returns `true` if they are identical, otherwise returns `false`.
 * {@link Initial} and {@link Pending} will return `false`.
 * {@link Done} will call {@link Eq.equals}.
 *
 * If you want to compare {@link Remote}'s values better use {@link getEq} or {@link getOrd} helpers.
 *
 * @category utils
 */
export const elem = (E) => (a) => (fa) => isDone(fa) && E.equals(a, fa.value);
/**
 * Takes a predicate and apply it to {@link Done} value.
 * {@link Initial} and {@link Pending} will return `false`.
 * @category utils
 */
export const exists = (predicate) => (fa) => isDone(fa) && predicate(fa.value);
/**
 * @category Instances
 */
export const getEq = (EA) => ({
    equals: (x, y) => pipe(x, fold(() => isInitial(y), () => isPending(y), (ax) => isDone(y) && EA.equals(ax, y.value))),
});
/**
 * @category Instances
 */
export const getMonoid = (SA) => ({
    concat: (x, y) => {
        switch (x._tag) {
            case 'initial': {
                return y;
            }
            case 'pending': {
                return isInitial(y) ? x : y;
            }
            case 'done': {
                return isDone(y) ? done(SA.concat(x.value, y.value)) : x;
            }
        }
    },
    empty: initial,
});
const constLt = constant(-1);
const constEq = constant(0);
const constGt = constant(1);
/**
 * @category Instances
 */
export const getOrd = (OA) => ({
    ...getEq(OA),
    compare: (x, y) => sign(pipe(x, fold(() => pipe(y, fold(constEq, constLt, constLt)), () => pipe(y, fold(constGt, constEq, constLt)), (xValue) => pipe(y, fold(constGt, constGt, (yValue) => OA.compare(xValue, yValue)))))),
});
/**
 * @category Instances
 */
export const getShow = (SA) => ({
    show: fold(() => 'initial', () => 'pending', (a) => `done(${SA.show(a)})`),
});
/**
 * @category traversing
 */
export const sequenceS = apply.sequenceS(Apply);
/**
 * @category traversing
 */
export const sequenceT = apply.sequenceT(Apply);
/**
 * @category traversing
 */
export const sequenceArray = array.sequence(Applicative);
/**
 * @category utils
 */
export const staleWhileRevalidate = (next) => (current) => pipe(next, fold(() => current, () => (isInitial(current) ? next : current), () => next));
//# sourceMappingURL=remote.js.map